/**
 * Standards (firm-model spec §3.2, §6.1): the firm's standards record, the per-field allow
 * mask, and the deck override shape. Declared once here; the server stores and resolves
 * them, the web panel edits the firm record, and (A4) the plugin sends a deck override.
 * A new field is one edit in this file (type + schema + STANDARDS_FIELDS), then
 * `npm run contract:sync`.
 *
 * Caps keep the prompt slot bounded; unknown keys are stripped, so a field can ship
 * client-first without breaking an older server.
 */
import { z } from "zod";
import { expectParity } from "../parity.js";
import {
  calendarYearFormatSchema,
  currencyFormatSchema,
  fiscalYearFormatSchema,
  languageVariantSchema,
  type CalendarYearFormat,
  type CurrencyFormat,
  type FiscalYearFormat,
  type LanguageVariant,
} from "./formats.js";

/** A brand color as `#RRGGBB`. The panel has no deck theme, so firm colors are literal. */
export type HexColor = string;

export type BrandPalette = {
  /** Chart series colors, in order. */
  series?: HexColor[];
  positive?: HexColor;
  negative?: HexColor;
  highlight?: HexColor;
};

export type BrandFonts = {
  /** Action titles and slide titles. */
  heading?: string;
  /** Other slide text and chart text. */
  body?: string;
  /** The heading size in whole points (6–96), as the Brand Profile's heading size (A8). */
  headingSizePt?: number;
  /** The body size in whole points (6–96): text boxes and chart text (D64's one text size). */
  bodySizePt?: number;
};

export type FirmBrand = {
  palette?: BrandPalette;
  fonts?: BrandFonts;
};

/** The standards values: the seven v1 fields (assistant-v4-spec §7) plus the firm brand
 * record (firm R7). Every field is optional; absent = no standard ("Not set"). The four
 * format fields are closed selects (standards selects spec): structured, never free text. */
export type FirmStandards = {
  /** One merged audience-and-purpose line ("Monthly updates for a non-technical board"). */
  audiencePurpose?: string;
  /** Exact strings for the deterministic lint. */
  bannedWords?: string[];
  languageVariant?: LanguageVariant;
  /** The house currency and how amounts are written (EUR, "€1.2M"). */
  currencyFormat?: CurrencyFormat;
  /** Calendar-year/date format ("Mar 2025"). */
  calendarYearFormat?: CalendarYearFormat;
  /** Fiscal-year format ("FY25") and, optionally, the month the fiscal year ends. */
  fiscalYearFormat?: FiscalYearFormat;
  /** Any other label rules ("percentages always whole numbers"). */
  otherLabelRules?: string;
  brand?: FirmBrand;
};

/** The unit a deck may override and the firm may allow or lock (spec §6.1). */
export type StandardsField =
  | "audiencePurpose"
  | "bannedWords"
  | "languageVariant"
  | "currencyFormat"
  | "calendarYearFormat"
  | "fiscalYearFormat"
  | "otherLabelRules"
  | "brandPalette"
  | "brandFonts";

export const STANDARDS_FIELDS: readonly StandardsField[] = [
  "audiencePurpose",
  "bannedWords",
  "languageVariant",
  "currencyFormat",
  "calendarYearFormat",
  "fiscalYearFormat",
  "otherLabelRules",
  "brandPalette",
  "brandFonts",
];

/** Per field, whether a deck may override it. An absent key = allowed (firm R6: default on). */
export type AllowMask = {
  audiencePurpose?: boolean;
  bannedWords?: boolean;
  languageVariant?: boolean;
  currencyFormat?: boolean;
  calendarYearFormat?: boolean;
  fiscalYearFormat?: boolean;
  otherLabelRules?: boolean;
  brandPalette?: boolean;
  brandFonts?: boolean;
};

/** A deck's own values (stored in the deck's config part, A4) and the revision that bumps
 * on each edit. An absent field inherits the firm's value. On the wire (turn context, AI
 * call body) it carries the seven text fields only: the deck's palette and fonts are its
 * Brand Profile, applied by the plugin's chart styling, and prompts render no brand. */
export type DeckStandardsOverride = {
  revision: number;
  standards: FirmStandards;
};

const hexColor = z.string().regex(/^#[0-9A-Fa-f]{6}$/);
const fontName = z.string().min(1).max(80);
/** The Brand Profile's size range (style-engine `clampChartFontSizePt`): whole points, 6–96. */
const fontSizePt = z.number().int().min(6).max(96);

export const firmStandardsSchema = z.object({
  audiencePurpose: z.string().max(300).optional(),
  bannedWords: z.array(z.string().min(1).max(40)).max(30).optional(),
  languageVariant: languageVariantSchema.optional(),
  currencyFormat: currencyFormatSchema.optional(),
  calendarYearFormat: calendarYearFormatSchema.optional(),
  fiscalYearFormat: fiscalYearFormatSchema.optional(),
  otherLabelRules: z.string().max(600).optional(),
  brand: z
    .object({
      palette: z
        .object({
          series: z.array(hexColor).max(12).optional(),
          positive: hexColor.optional(),
          negative: hexColor.optional(),
          highlight: hexColor.optional(),
        })
        .optional(),
      fonts: z
        .object({
          heading: fontName.optional(),
          body: fontName.optional(),
          headingSizePt: fontSizePt.optional(),
          bodySizePt: fontSizePt.optional(),
        })
        .optional(),
    })
    .optional(),
});

export const allowMaskSchema = z.object({
  audiencePurpose: z.boolean().optional(),
  bannedWords: z.boolean().optional(),
  languageVariant: z.boolean().optional(),
  currencyFormat: z.boolean().optional(),
  calendarYearFormat: z.boolean().optional(),
  fiscalYearFormat: z.boolean().optional(),
  otherLabelRules: z.boolean().optional(),
  brandPalette: z.boolean().optional(),
  brandFonts: z.boolean().optional(),
});

export const deckStandardsOverrideSchema = z.object({
  revision: z.number().int().min(0),
  standards: firmStandardsSchema,
});

expectParity<z.infer<typeof firmStandardsSchema>, FirmStandards>(true);
expectParity<z.infer<typeof allowMaskSchema>, AllowMask>(true);
expectParity<z.infer<typeof deckStandardsOverrideSchema>, DeckStandardsOverride>(true);

/**
 * A stored or received standards record, field by field (standards selects spec §2.1): a
 * field that fails its schema (staging's old free-text `$1.2M`, a hand-edited deck part, a
 * cap tightened since) reads as not set, and the valid fields stay. Standards shape taste
 * and must never break a turn, a call or the panel. Not an object = nothing set.
 */
export function parseStandardsByField(raw: unknown): FirmStandards {
  const out: Record<string, unknown> = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out as FirmStandards;
  const values = raw as Record<string, unknown>;
  const shape = firmStandardsSchema.shape as Record<string, z.ZodTypeAny>;
  const keys = Object.keys(shape);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (values[key] === undefined) continue;
    const parsed = shape[key].safeParse(values[key]);
    if (parsed.success && parsed.data !== undefined) out[key] = parsed.data;
  }
  return out as FirmStandards;
}

/** The deck override as the turn context and the AI call body carry it: its standards
 * parse field by field (a bad field drops alone, an older bundle's free text included); a
 * bad revision fails the whole override, which the routes then drop. */
export const deckStandardsOverrideWireSchema = z.object({
  revision: z.number().int().min(0),
  standards: z.unknown().transform(parseStandardsByField),
});

expectParity<z.output<typeof deckStandardsOverrideWireSchema>, DeckStandardsOverride>(true);

/** `GET /v1/firm/standards` and the answer to a save (firm-model spec §6.2). */
export type FirmStandardsRecord = {
  standards: FirmStandards;
  allow: AllowMask;
  /** Bumps on every save; 0 = the firm has never saved standards. */
  version: number;
  updatedAt: string | null;
  /** Who saved last, for "Last changed by X"; null when unknown or since deleted. */
  updatedByEmail: string | null;
};

const firmStandardsRecordSchema = z.object({
  standards: firmStandardsSchema,
  allow: allowMaskSchema,
  version: z.number().int(),
  updatedAt: z.string().nullable(),
  updatedByEmail: z.string().nullable(),
});

expectParity<z.infer<typeof firmStandardsRecordSchema>, FirmStandardsRecord>(true);

/** The firm record as a client reads it (the plugin, the panel): its standards parse field
 * by field like the server's own read, so under deploy skew one stale field reads as not
 * set instead of failing the whole view. */
export const firmStandardsRecordReadSchema = firmStandardsRecordSchema.extend({
  standards: z.unknown().transform(parseStandardsByField),
});

expectParity<z.output<typeof firmStandardsRecordReadSchema>, FirmStandardsRecord>(true);

/** `PUT /v1/firm/standards` (`edit_standards`). `version` is the one the editor loaded: a
 * save over a newer version is refused (`stale_version`) rather than silently lost. */
export type FirmStandardsSaveRequest = {
  standards: FirmStandards;
  allow: AllowMask;
  version: number;
};

export const firmStandardsSaveRequestSchema = z.object({
  standards: firmStandardsSchema,
  allow: allowMaskSchema,
  version: z.number().int().min(0),
});

expectParity<z.infer<typeof firmStandardsSaveRequestSchema>, FirmStandardsSaveRequest>(true);
